import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { get } from "lodash";

import { useWorkspace } from "src/contexts/global/WorkspaceContext";
import type { AttributePolicyByKey } from "src/pages/OrgDesignSettings/attributeDefaultsTypes";
import { parsePersistedAttributePolicies } from "src/pages/OrgDesignSettings/attributePolicy";
import { getOrgDesignWorkspaceConfig } from "src/queries/orgDesignWorkspaceConfig.graphql";
import {
  resolveDefaultInsightsHierarchyView,
  type InsightsHierarchyView,
} from "src/util/insightsHierarchyView";

import {
  resolveInsightsHierarchy,
  type InsightsHierarchyConfig,
} from "./insightsHierarchyOptions";
import {
  resolveInsightsTargets,
  type InsightsTargetsConfig,
} from "./insightsTargets";

export type OrgDesignConfig = {
  ready: boolean;
  loading: boolean;
  hierarchy: InsightsHierarchyConfig;
  targets: InsightsTargetsConfig;
  showTargets: boolean;
  allowLensSwitching: boolean;
  defaultView: InsightsHierarchyView;
  defaultViewRaw: unknown;
  insightsHierarchyRaw: unknown;
  attributePolicies: AttributePolicyByKey;
};

/**
 * Org Design workspace config (insightsHierarchy + default lens + attribute policies).
 */
export const useOrgDesignConfig = (): OrgDesignConfig => {
  const workspace = useWorkspace();
  const { data, loading } = useQuery(getOrgDesignWorkspaceConfig, {
    errorPolicy: "ignore",
    fetchPolicy: "cache-and-network",
    variables: { workspaceId: workspace?.id },
    skip: !workspace?.id,
  });

  const insightsHierarchyRaw =
    get(data, "workspace.config.insightsHierarchy") ??
    workspace?.config?.insightsHierarchy;
  const defaultViewRaw =
    get(data, "workspace.config.defaultInsightsHierarchyView") ??
    workspace?.config?.defaultInsightsHierarchyView;
  const attributePoliciesRaw =
    get(data, "workspace.config.orgChartAttributePolicies") ??
    workspace?.config?.orgChartAttributePolicies;

  const settled =
    Boolean(workspace?.id) &&
    (!loading || data !== undefined || insightsHierarchyRaw !== undefined);

  const hierarchy = useMemo(
    () => resolveInsightsHierarchy(insightsHierarchyRaw),
    [insightsHierarchyRaw]
  );
  const targets = useMemo(() => {
    const rawTargets =
      insightsHierarchyRaw &&
      typeof insightsHierarchyRaw === "object" &&
      "targets" in insightsHierarchyRaw
        ? (insightsHierarchyRaw as { targets?: unknown }).targets
        : undefined;
    return resolveInsightsTargets(rawTargets);
  }, [insightsHierarchyRaw]);
  const defaultView = useMemo(
    () => resolveDefaultInsightsHierarchyView(defaultViewRaw),
    [defaultViewRaw]
  );
  const attributePolicies = useMemo(
    () => parsePersistedAttributePolicies(attributePoliciesRaw),
    [attributePoliciesRaw]
  );

  return {
    ready: settled,
    loading,
    hierarchy,
    targets,
    showTargets: settled && hierarchy.showTargets,
    allowLensSwitching: hierarchy.allowLensSwitching,
    defaultView,
    defaultViewRaw,
    insightsHierarchyRaw,
    attributePolicies,
  };
};
